import React from "react";
import { Box } from "@mui/material";
import { Token } from "../theme";

export interface SegmentedOption<T> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

interface Props<T> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  /** Rendered above the control, matching MuiInputLabel, so it lines up with the fields beside it. */
  label?: string;
  size?: "sm" | "md";
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  compact?: boolean;
  activeColor?: string;
}

export const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  lineHeight: "16px",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: ".06em",
  fontWeight: 600,
  color: Token.textTertiary,
  whiteSpace: "nowrap",
};

export const TRACK_HEIGHT = { sm: 28, md: 34 };
export const ITEM_HEIGHT = { sm: 24, md: 28 };
export const FONT_SIZE = { sm: 11.5, md: 12.5 };

export function SegmentedControl<T extends string | number | boolean>({
  value,
  options,
  onChange,
  label,
  size = "md",
  ariaLabel,
  className,
  style,
  compact = false,
  activeColor = Token.actionPrimaryActive,
}: Props<T>) {
  const track = (
    <Box
      role="group"
      aria-label={ariaLabel}
      className={className}
      style={{
        display: "inline-flex",
        gap: 6,
        padding: 2,
        height: TRACK_HEIGHT[size],
        boxSizing: "border-box",
        background: Token.surfaceSunken,
        border: `1px solid ${Token.borderDefault}`,
        borderRadius: compact ? 6 : 999,
        flexShrink: 0,
        ...style,
      }}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={index}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: ITEM_HEIGHT[size],
              padding: "0 11px",
              border: "none",
              borderRadius: compact ? 4 : 999,
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
              fontSize: FONT_SIZE[size],
              fontWeight: active ? 600 : 400,
              background: active ? activeColor : "transparent",
              color: active ? Token.textOnAction : Token.textTertiary,
              boxShadow: active ? "0 1px 2px rgba(16,24,40,.08)" : "none",
              transition: "all 0.2s ease",
            }}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </Box>
  );

  if (!label) return track;

  return (
    <Box style={{ display: "inline-flex", flexDirection: "column", flexShrink: 0 }}>
      <span style={FIELD_LABEL_STYLE}>{label}</span>
      {track}
    </Box>
  );
}

interface BooleanSegmentedProps {
  checked: boolean | undefined;
  onToggle: () => void;
  offLabel: string;
  onLabel: string;
  label?: string;
  size?: "sm" | "md";
  ariaLabel?: string;
}

export const BooleanSegmented: React.FC<BooleanSegmentedProps> = ({
  checked,
  onToggle,
  offLabel,
  onLabel,
  label,
  size = "md",
  ariaLabel,
}) => (
  <SegmentedControl<boolean>
    value={!!checked}
    label={label}
    options={[
      { value: false, label: offLabel },
      { value: true, label: onLabel },
    ]}
    onChange={(next) => {
      if (next !== !!checked) onToggle();
    }}
    size={size}
    ariaLabel={ariaLabel}
  />
);
